import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { Plus, Trash2, Save, Settings as SettingsIcon, RotateCcw, Palette, Tags, Image as ImageIcon, Mail, Send, Clock, History, UserPlus, Copy, Check, ExternalLink, BarChart3, Facebook } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { invalidateSettings } from '../hooks/useSettings';
import { ImageUpload } from '../components/ImageUpload';
import { ESTADOS } from '../components/StateCitySelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: 'lideranca_tipos', title: 'Tipos de Liderança', description: 'Categorias usadas no cadastro de lideranças' },
  { key: 'destino_tipos', title: 'Tipos de Destino', description: 'Tipos disponíveis (Canal, Grupo, Comunidade...)' },
  { key: 'link_tipos_acao', title: 'Tipos de Ação dos Links', description: 'Classificação dos links curtos' },
];

const AllowedStatesEditor = ({ selected, onChange, disabled }) => {
  const isSelected = (sigla) => selected.includes(sigla);
  const toggle = (sigla) => {
    if (disabled) return;
    if (isSelected(sigla)) {
      onChange(selected.filter((s) => s !== sigla));
    } else {
      onChange([...selected, sigla]);
    }
  };
  const allSelected = selected.length === ESTADOS.length;
  const noneSelected = selected.length === 0;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || noneSelected}
          onClick={() => onChange([])}
          data-testid="states-clear-btn"
        >
          Limpar
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || allSelected}
          onClick={() => onChange(ESTADOS.map((e) => e.sigla))}
          data-testid="states-all-btn"
        >
          Selecionar todos
        </Button>
        <span className="text-xs text-muted-foreground ml-2">
          {noneSelected ? 'Nenhum (todos disponíveis)' : `${selected.length} selecionado(s)`}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {ESTADOS.map((e) => {
          const on = isSelected(e.sigla);
          return (
            <button
              type="button"
              key={e.sigla}
              onClick={() => toggle(e.sigla)}
              disabled={disabled}
              className={`text-left rounded-lg border px-3 py-2 text-xs transition-colors ${
                on
                  ? 'bg-primary/5 border-primary text-primary'
                  : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
              } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              data-testid={`state-toggle-${e.sigla}`}
            >
              <div className="font-bold">{e.sigla}</div>
              <div className="text-[10px] text-muted-foreground truncate">{e.nome}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ListEditor = ({ title, description, items, onChange, disabled }) => {
  const [newItem, setNewItem] = useState('');

  const addItem = () => {
    const value = newItem.trim();
    if (!value) return;
    if (items.includes(value)) {
      toast.error('Esse item já existe');
      return;
    }
    onChange([...items, value]);
    setNewItem('');
  };

  const removeItem = (index) => {
    if (items.length <= 1) {
      toast.error('A lista deve ter pelo menos 1 item');
      return;
    }
    const updated = items.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!disabled && (
          <div className="flex gap-2">
            <Input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder="Adicionar novo item..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addItem();
                }
              }}
              data-testid={`add-${title.replace(/\s+/g, '-').toLowerCase()}-input`}
            />
            <Button type="button" size="icon" onClick={addItem}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
        <div className="space-y-2">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-3 py-2">
              <span className="text-sm text-slate-800">{item}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className="text-rose-500 hover:text-rose-700"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const ConfiguracoesPage = () => {
  const { user, checkAuth, activeTenant } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [settings, setSettings] = useState(null);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [branding, setBranding] = useState({
    primary_color: '#1e3a8a',
    logo_url: '',
    footer_text: '',
  });
  const [originalBranding, setOriginalBranding] = useState(branding);
  const [savingBranding, setSavingBranding] = useState(false);

  const [reportEmailInput, setReportEmailInput] = useState('');
  const [sendingNow, setSendingNow] = useState({ daily: false, weekly: false });
  const [reportHistory, setReportHistory] = useState([]);

  const [destinations, setDestinations] = useState([]);
  const [signupLinkCopied, setSignupLinkCopied] = useState(false);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, t, h, d] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/settings`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tenant/current`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/reports/history`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/destinations`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSettings(s.data);
      setOriginalSettings(JSON.parse(JSON.stringify(s.data)));
      const b = {
        primary_color: t.data.primary_color || '#1e3a8a',
        logo_url: t.data.logo_url || '',
        footer_text: t.data.footer_text || '',
      };
      setBranding(b);
      setOriginalBranding(b);
      setReportHistory(h.data || []);
      setDestinations(d.data || []);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/reports/history`, { withCredentials: true });
      setReportHistory(data || []);
    } catch (err) {
      /* ignore */
    }
  };

  const handleSendNow = async (period) => {
    setSendingNow((p) => ({ ...p, [period]: true }));
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/reports/send-now?period=${period}`,
        {},
        { withCredentials: true }
      );
      toast.success(data.message || 'Relatório enviado');
      await fetchHistory();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao enviar relatório');
    } finally {
      setSendingNow((p) => ({ ...p, [period]: false }));
    }
  };

  const addReportEmail = () => {
    const email = reportEmailInput.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('E-mail inválido');
      return;
    }
    const existing = settings?.report_recipients || [];
    if (existing.includes(email)) {
      toast.error('E-mail já adicionado');
      return;
    }
    setSettings({ ...settings, report_recipients: [...existing, email] });
    setReportEmailInput('');
  };

  const removeReportEmail = (idx) => {
    const existing = settings?.report_recipients || [];
    setSettings({ ...settings, report_recipients: existing.filter((_, i) => i !== idx) });
  };

  const handleSectionChange = (key, newItems) => {
    setSettings({ ...settings, [key]: newItems });
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/settings`, settings, { withCredentials: true });
      setSettings(data);
      setOriginalSettings(JSON.parse(JSON.stringify(data)));
      invalidateSettings();
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      await axios.put(`${BACKEND_URL}/api/tenant/branding`, branding, { withCredentials: true });
      setOriginalBranding({ ...branding });
      await checkAuth(); // refresh active_tenant in context
      toast.success('Personalização salva!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao salvar marca');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleResetSettings = () => {
    if (window.confirm('Descartar todas as alterações não salvas?')) {
      setSettings(JSON.parse(JSON.stringify(originalSettings)));
    }
  };

  const hasSettingsChanges =
    settings && originalSettings && JSON.stringify(settings) !== JSON.stringify(originalSettings);
  const hasBrandingChanges =
    JSON.stringify(branding) !== JSON.stringify(originalBranding);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="configuracoes-page">
        <div>
          <h1 className="text-4xl font-black tracking-tight flex items-center gap-3">
            <SettingsIcon className="h-8 w-8" />
            Configurações
          </h1>
          <p className="text-muted-foreground mt-2">
            Personalize a aparência e as listas de categorias do seu cliente
          </p>
        </div>

        {!isAdmin && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="py-4">
              <p className="text-sm text-yellow-800">
                Você está visualizando em modo somente leitura. Apenas administradores podem editar.
              </p>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="branding" className="w-full">
          <TabsList>
            <TabsTrigger value="branding" data-testid="tab-branding">
              <Palette className="h-4 w-4 mr-2" /> Personalização
            </TabsTrigger>
            <TabsTrigger value="signup" data-testid="tab-signup">
              <UserPlus className="h-4 w-4 mr-2" /> Cadastro Público
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">
              <Mail className="h-4 w-4 mr-2" /> Relatórios automáticos
            </TabsTrigger>
            <TabsTrigger value="integrations" data-testid="tab-integrations">
              <BarChart3 className="h-4 w-4 mr-2" /> Integrações
            </TabsTrigger>
            <TabsTrigger value="lists" data-testid="tab-lists">
              <Tags className="h-4 w-4 mr-2" /> Tipos &amp; Categorias
            </TabsTrigger>
          </TabsList>

          <TabsContent value="branding" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5 text-indigo-600" />
                  Marca do cliente
                </CardTitle>
                <CardDescription>
                  Personalize a aparência do sistema. Esses dados aparecem apenas dentro da plataforma (não nas páginas públicas de redirecionamento).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <ImageUpload
                      label="Logotipo do cliente"
                      value={branding.logo_url}
                      onChange={(url) => setBranding({ ...branding, logo_url: url })}
                      folder={`meadd/tenants/${activeTenant?.id || 'unknown'}/logo`}
                      aspect="aspect-[3/1]"
                      maxWidth="max-w-[14rem]"
                      testId="branding-logo"
                    />
                    <p className="text-xs text-slate-500">
                      Faça upload do logotipo (PNG/JPG/SVG, fundo transparente recomendado, ~200×60px). Esta imagem aparece nas previsualizações de WhatsApp.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="primary_color">Cor principal</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        id="primary_color"
                        type="color"
                        value={branding.primary_color}
                        onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                        className="w-16 h-10 p-1 cursor-pointer"
                        disabled={!isAdmin}
                        data-testid="branding-color-input"
                      />
                      <Input
                        value={branding.primary_color}
                        onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                        placeholder="#1e3a8a"
                        disabled={!isAdmin}
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      Cor usada em destaques visuais (avatar do cliente, links e ações principais).
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="footer_text">Rodapé interno</Label>
                  <Textarea
                    id="footer_text"
                    value={branding.footer_text}
                    onChange={(e) => setBranding({ ...branding, footer_text: e.target.value })}
                    placeholder="Ex: Sistema gerenciado pela Equipe XPTO — Suporte: suporte@xpto.com"
                    rows={3}
                    disabled={!isAdmin}
                    data-testid="branding-footer-input"
                  />
                  <p className="text-xs text-slate-500">
                    Texto exibido no rodapé das páginas internas do sistema.
                  </p>
                </div>

                {isAdmin && (
                  <div className="flex justify-end gap-2 pt-2">
                    {hasBrandingChanges && (
                      <Button
                        variant="outline"
                        onClick={() => setBranding({ ...originalBranding })}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Descartar
                      </Button>
                    )}
                    <Button
                      onClick={handleSaveBranding}
                      disabled={!hasBrandingChanges || savingBranding}
                      data-testid="save-branding-btn"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {savingBranding ? 'Salvando...' : 'Salvar personalização'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="signup" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-indigo-600" />
                  Cadastro público de lideranças
                </CardTitle>
                <CardDescription>
                  Compartilhe um link único para que novas lideranças se cadastrem sozinhas.
                  Cada uma receberá um link personalizado e QR Code logo após o cadastro,
                  apontando para o destino padrão definido abaixo.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Default destination selector */}
                <div className="space-y-2">
                  <Label>Destino padrão *</Label>
                  <Select
                    value={settings?.default_destination_id || ''}
                    onValueChange={(v) => setSettings({ ...settings, default_destination_id: v })}
                    disabled={!isAdmin}
                  >
                    <SelectTrigger data-testid="default-destination-select">
                      <SelectValue placeholder="Selecione um destino..." />
                    </SelectTrigger>
                    <SelectContent>
                      {destinations.length === 0 ? (
                        <div className="p-3 text-sm text-slate-500 text-center">
                          Nenhum destino cadastrado ainda.
                        </div>
                      ) : (
                        destinations
                          .filter((d) => d.status === 'ativo')
                          .map((d) => (
                            <SelectItem key={d.id} value={d.id}>
                              {d.nome} <span className="text-slate-400 ml-2 text-xs">({d.tipo})</span>
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Esse destino será usado automaticamente em todos os links gerados via cadastro público.
                    {!settings?.default_destination_id && (
                      <span className="text-rose-600 font-medium"> Obrigatório para ativar o cadastro público.</span>
                    )}
                  </p>
                </div>

                {/* Toggle */}
                <div className="flex items-center justify-between border border-slate-200 rounded-lg p-4">
                  <div>
                    <div className="font-semibold text-slate-900">Cadastro público ativo</div>
                    <div className="text-xs text-slate-500">
                      Quando desativado, o link público retorna erro 403.
                    </div>
                  </div>
                  <Switch
                    checked={settings?.leader_signup_enabled !== false}
                    onCheckedChange={(v) => setSettings({ ...settings, leader_signup_enabled: v })}
                    disabled={!isAdmin || !settings?.default_destination_id}
                    data-testid="toggle-leader-signup"
                  />
                </div>

                {/* Personalização da página */}
                <div className="space-y-4 border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <ImageIcon className="h-4 w-4 text-indigo-600" />
                    Personalização da página de cadastro
                  </div>
                  <p className="text-xs text-slate-500 -mt-2">
                    Esses campos aparecem para quem abrir o link público de cadastro.
                  </p>

                  <ImageUpload
                    label="Banner da página"
                    value={settings?.signup_hero_image_url || ''}
                    onChange={(url) => setSettings({ ...settings, signup_hero_image_url: url })}
                    folder={`meadd/signup/${activeTenant?.id || 'unknown'}/hero`}
                    aspect="aspect-video"
                    maxWidth="max-w-xs"
                    testId="signup-hero"
                  />

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Título</Label>
                    <Input
                      value={settings?.signup_title || ''}
                      onChange={(e) => setSettings({ ...settings, signup_title: e.target.value })}
                      placeholder="Ex: Junte-se à nossa rede"
                      maxLength={80}
                      disabled={!isAdmin}
                      data-testid="signup-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Subtítulo</Label>
                    <Textarea
                      value={settings?.signup_subtitle || ''}
                      onChange={(e) => setSettings({ ...settings, signup_subtitle: e.target.value })}
                      placeholder="Descrição curta exibida abaixo do título"
                      maxLength={240}
                      rows={2}
                      disabled={!isAdmin}
                      data-testid="signup-subtitle-input"
                    />
                  </div>
                </div>

                {/* Public URL */}
                {activeTenant?.slug && settings?.default_destination_id && settings?.leader_signup_enabled !== false && (() => {
                  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
                  const publicUrl = `${apiBase}/api/og/cadastrar/${activeTenant.slug}`;
                  const previewTitle = settings?.signup_title || `Seja uma liderança em ${activeTenant.name}`;
                  const previewDesc = settings?.signup_subtitle || `Cadastre-se como liderança em ${activeTenant.name} e ganhe um link personalizado para convidar apoiadores.`;
                  const previewImage = settings?.signup_hero_image_url || activeTenant?.logo_url || '/brand/meadd-logo.png';
                  const waText = `Olá! Você foi convidado(a) para se cadastrar como liderança em ${activeTenant.name}. Acesse: ${publicUrl}`;
                  return (
                    <div className="space-y-3">
                      <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-4 space-y-3">
                        <div className="flex items-start gap-2">
                          <div className="h-8 w-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center shrink-0">
                            <ExternalLink className="h-4 w-4" />
                          </div>
                          <div>
                            <div className="font-bold text-emerald-900 text-sm">
                              Link de cadastro público (com preview personalizado no WhatsApp)
                            </div>
                            <p className="text-xs text-emerald-800/80 mt-0.5">
                              <strong>Sempre compartilhe esta URL</strong> — ela faz o WhatsApp exibir a imagem, título e subtítulo configurados acima.
                              Se você copiar a URL da barra de endereços (sem <code className="text-[10px] bg-white px-1 rounded">/api/og</code>) o preview ficará genérico.
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            value={publicUrl}
                            readOnly
                            className="font-mono text-xs bg-white"
                            data-testid="signup-public-url"
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            onClick={async () => {
                              await navigator.clipboard.writeText(publicUrl);
                              setSignupLinkCopied(true);
                              toast.success('Link copiado!');
                              setTimeout(() => setSignupLinkCopied(false), 2000);
                            }}
                            data-testid="copy-signup-url-btn"
                          >
                            {signupLinkCopied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                          </Button>
                          <a
                            href={publicUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center px-3 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                            title="Abrir"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </div>
                        <a
                          href={`https://wa.me/?text=${encodeURIComponent(waText)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
                          data-testid="share-signup-wa-btn"
                        >
                          <Send className="h-4 w-4" />
                          Compartilhar no WhatsApp
                        </a>
                      </div>

                      {/* Preview Card */}
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-slate-500">
                          Pré-visualização (como aparecerá no WhatsApp)
                        </Label>
                        <div className="rounded-xl border border-slate-200 bg-slate-900 p-3 max-w-md">
                          <div className="flex bg-slate-800 rounded-md overflow-hidden">
                            <div className="w-28 h-28 bg-white shrink-0 flex items-center justify-center overflow-hidden">
                              <img
                                src={previewImage}
                                alt="Preview"
                                className="w-full h-full object-contain"
                                onError={(e) => { e.target.style.opacity = 0.2; }}
                              />
                            </div>
                            <div className="p-3 min-w-0 flex-1">
                              <div className="text-white font-semibold text-sm leading-tight line-clamp-2">
                                {previewTitle}
                              </div>
                              <div className="text-slate-300 text-xs mt-1 line-clamp-2">
                                {previewDesc}
                              </div>
                              <div className="text-slate-500 text-[11px] mt-2 truncate">
                                {(publicUrl.match(/^https?:\/\/([^/]+)/) || ['', ''])[1]}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {(!settings?.default_destination_id || destinations.length === 0) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                    {destinations.length === 0
                      ? 'Cadastre primeiro pelo menos um destino (Canal/Grupo do WhatsApp) em Destinos.'
                      : 'Defina um destino padrão para ativar o cadastro público.'}
                  </div>
                )}

                {/* === Personalização da página de captação de apoiadores === */}
                <div className="space-y-4 border border-slate-200 rounded-lg p-4 bg-emerald-50/30">
                  <div className="flex items-start gap-2">
                    <ImageIcon className="h-4 w-4 text-emerald-700 mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        Personalização da página de captação de apoiadores
                      </div>
                      <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                        Esta é a página que <strong>os apoiadores veem ao clicar no link de uma liderança</strong>. Estes valores serão usados como padrão; cada link pode ter um override próprio em <strong>Links → Personalizar</strong>.
                      </p>
                    </div>
                  </div>

                  <ImageUpload
                    label="Imagem de cabeçalho (1200×628 recomendada)"
                    value={settings?.capture_hero_image_url || ''}
                    onChange={(url) => setSettings({ ...settings, capture_hero_image_url: url })}
                    folder={`meadd/tenants/${activeTenant?.id || 'unknown'}/capture`}
                    aspect="aspect-[1200/628]"
                    maxWidth="max-w-md"
                    testId="capture-hero"
                  />

                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-slate-500">
                      Mensagem antes dos campos
                    </Label>
                    <Textarea
                      value={settings?.capture_subtitle || ''}
                      onChange={(e) => setSettings({ ...settings, capture_subtitle: e.target.value })}
                      placeholder="Ex.: Junte-se à nossa rede de apoiadores e ajude a transformar Goiânia! Preencha seus dados e entre no grupo oficial."
                      maxLength={240}
                      rows={3}
                      disabled={!isAdmin}
                      data-testid="capture-subtitle-input"
                    />
                    <p className="text-[11px] text-slate-500">
                      {(settings?.capture_subtitle || '').length}/240. Em branco usaremos a mensagem padrão "Preencha seus dados para continuar e entrar no grupo/canal do WhatsApp."
                    </p>
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    {hasSettingsChanges && (
                      <Button variant="outline" onClick={handleResetSettings}>
                        <RotateCcw className="h-4 w-4 mr-2" /> Descartar
                      </Button>
                    )}
                    <Button onClick={handleSaveSettings} disabled={!hasSettingsChanges || saving} data-testid="save-signup-btn">
                      <Save className="h-4 w-4 mr-2" /> {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-indigo-600" />
                  Envio automático de relatórios
                </CardTitle>
                <CardDescription>
                  Receba periodicamente por e-mail as planilhas CSV de <strong>lideranças</strong> e <strong>apoiadores</strong> com resumo do período.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Toggles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-slate-900">Relatório diário</div>
                        <div className="text-xs text-slate-500">Enviado todo dia no horário definido</div>
                      </div>
                      <Switch
                        checked={!!settings?.report_daily_enabled}
                        onCheckedChange={(v) => setSettings({ ...settings, report_daily_enabled: v })}
                        disabled={!isAdmin}
                        data-testid="toggle-daily-report"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendNow('daily')}
                      disabled={sendingNow.daily || !isAdmin}
                      data-testid="send-now-daily-btn"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {sendingNow.daily ? 'Enviando...' : 'Enviar agora'}
                    </Button>
                  </div>

                  <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-slate-900">Relatório semanal</div>
                        <div className="text-xs text-slate-500">Enviado uma vez por semana</div>
                      </div>
                      <Switch
                        checked={!!settings?.report_weekly_enabled}
                        onCheckedChange={(v) => setSettings({ ...settings, report_weekly_enabled: v })}
                        disabled={!isAdmin}
                        data-testid="toggle-weekly-report"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendNow('weekly')}
                      disabled={sendingNow.weekly || !isAdmin}
                      data-testid="send-now-weekly-btn"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {sendingNow.weekly ? 'Enviando...' : 'Enviar agora'}
                    </Button>
                  </div>
                </div>

                {/* Agenda */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Clock className="h-4 w-4" /> Horário de envio (UTC)
                    </Label>
                    <Select
                      value={String(settings?.report_send_hour ?? 8)}
                      onValueChange={(v) => setSettings({ ...settings, report_send_hour: parseInt(v) })}
                      disabled={!isAdmin}
                    >
                      <SelectTrigger data-testid="select-send-hour">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }).map((_, h) => (
                          <SelectItem key={h} value={String(h)}>
                            {String(h).padStart(2, '0')}:00 UTC
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Horário de Brasília = UTC-3 (ex: 11:00 UTC = 08:00 BRT)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>Dia da semana (para envio semanal)</Label>
                    <Select
                      value={String(settings?.report_weekly_day ?? 0)}
                      onValueChange={(v) => setSettings({ ...settings, report_weekly_day: parseInt(v) })}
                      disabled={!isAdmin}
                    >
                      <SelectTrigger data-testid="select-weekly-day">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Segunda-feira</SelectItem>
                        <SelectItem value="1">Terça-feira</SelectItem>
                        <SelectItem value="2">Quarta-feira</SelectItem>
                        <SelectItem value="3">Quinta-feira</SelectItem>
                        <SelectItem value="4">Sexta-feira</SelectItem>
                        <SelectItem value="5">Sábado</SelectItem>
                        <SelectItem value="6">Domingo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Destinatários */}
                <div className="space-y-2">
                  <Label>Destinatários</Label>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={reportEmailInput}
                      onChange={(e) => setReportEmailInput(e.target.value)}
                      placeholder="email@exemplo.com"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addReportEmail();
                        }
                      }}
                      disabled={!isAdmin}
                      data-testid="report-email-input"
                    />
                    <Button type="button" size="icon" onClick={addReportEmail} disabled={!isAdmin}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {(settings?.report_recipients || []).length === 0 ? (
                    <p className="text-xs text-slate-500 mt-2">
                      Adicione pelo menos um e-mail para receber os relatórios.
                    </p>
                  ) : (
                    <div className="space-y-2 mt-2">
                      {(settings?.report_recipients || []).map((email, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-3 py-2"
                          data-testid={`report-recipient-${idx}`}
                        >
                          <span className="text-sm">{email}</span>
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => removeReportEmail(idx)}
                              className="text-rose-500 hover:text-rose-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {isAdmin && (
                  <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    {hasSettingsChanges && (
                      <Button variant="outline" onClick={handleResetSettings}>
                        <RotateCcw className="h-4 w-4 mr-2" /> Descartar
                      </Button>
                    )}
                    <Button onClick={handleSaveSettings} disabled={!hasSettingsChanges || saving} data-testid="save-reports-btn">
                      <Save className="h-4 w-4 mr-2" /> {saving ? 'Salvando...' : 'Salvar agenda'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Histórico */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Últimos envios
                </CardTitle>
                <CardDescription>{reportHistory.length} envios registrados</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {reportHistory.length === 0 ? (
                  <div className="text-center py-10 text-sm text-slate-500">
                    Nenhum relatório enviado ainda.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          <th className="py-3 px-4">Data</th>
                          <th className="py-3 px-4">Tipo</th>
                          <th className="py-3 px-4">Destinatários</th>
                          <th className="py-3 px-4">Estatísticas</th>
                          <th className="py-3 px-4">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportHistory.map((h, idx) => {
                          const dt = new Date(h.sent_at);
                          return (
                            <tr key={idx} className="border-t border-slate-100">
                              <td className="py-3 px-4 tabular-nums">
                                {dt.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                              </td>
                              <td className="py-3 px-4">
                                <Badge variant="outline" className="capitalize">
                                  {h.period === 'daily' ? 'Diário' : 'Semanal'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-xs text-slate-600">
                                {(h.recipients || []).join(', ')}
                              </td>
                              <td className="py-3 px-4 text-xs text-slate-600">
                                {h.stats ? (
                                  <span>
                                    {h.stats.total_acessos} acessos · {h.stats.novos_identificados} apoiadores
                                  </span>
                                ) : '—'}
                              </td>
                              <td className="py-3 px-4">
                                {h.status === 'sent' ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Enviado</Badge>
                                ) : (
                                  <Badge className="bg-rose-100 text-rose-700 border-rose-200">Falhou</Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="integrations" className="mt-6 space-y-6">
            <Card data-testid="meta-pixel-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Facebook className="h-5 w-5 text-[#1877F2]" />
                  Meta Pixel (Facebook / Instagram Ads)
                </CardTitle>
                <CardDescription>
                  Rastreie conversões em <strong>suas páginas públicas</strong> (formulários de captação
                  e cadastros de liderança) para otimizar campanhas no Gerenciador de Anúncios da Meta.
                  Os eventos <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">PageView</code>,{' '}
                  <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">Lead</code> e{' '}
                  <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">CompleteRegistration</code>{' '}
                  são disparados automaticamente. Nada é enviado a partir do painel administrativo — {' '}
                  isolamento total por cliente.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4 text-xs text-blue-900 leading-relaxed">
                  <p className="font-semibold mb-1">Como obter seu Pixel ID:</p>
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Acesse o <a href="https://business.facebook.com/events_manager" target="_blank" rel="noreferrer" className="underline font-medium">Gerenciador de Eventos da Meta</a>.</li>
                    <li>Crie ou selecione um Pixel para o seu site/perfil.</li>
                    <li>Copie o número identificador (10–20 dígitos) e cole no campo abaixo.</li>
                  </ol>
                </div>

                <div>
                  <Label htmlFor="meta-pixel-id-input" className="text-sm font-semibold">
                    Meta Pixel ID
                  </Label>
                  <Input
                    id="meta-pixel-id-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="Ex.: 1234567890123456"
                    value={settings?.meta_pixel_id || ''}
                    onChange={(e) => setSettings({ ...settings, meta_pixel_id: e.target.value.replace(/\D/g, '') })}
                    disabled={!isAdmin}
                    className="mt-1.5 max-w-md font-mono"
                    data-testid="meta-pixel-id-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Somente números. Deixe em branco para <strong>desativar</strong> o rastreamento.
                  </p>
                </div>

                <div>
                  <Label htmlFor="meta-pixel-test-event-code-input" className="text-sm font-semibold">
                    Test Event Code <span className="text-slate-400 font-normal">(opcional)</span>
                  </Label>
                  <Input
                    id="meta-pixel-test-event-code-input"
                    type="text"
                    placeholder="Ex.: TEST12345"
                    value={settings?.meta_pixel_test_event_code || ''}
                    onChange={(e) => setSettings({ ...settings, meta_pixel_test_event_code: e.target.value })}
                    disabled={!isAdmin}
                    className="mt-1.5 max-w-md font-mono"
                    data-testid="meta-pixel-test-event-code-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Use somente durante testes na aba <em>Test Events</em> do Gerenciador de Eventos.
                    Remova antes de ir ao ar em produção.
                  </p>
                </div>

                {settings?.meta_pixel_id && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 flex items-center gap-2">
                    <Check className="h-4 w-4" />
                    <span>
                      Pixel ativo — eventos serão disparados nas páginas públicas deste cliente.
                    </span>
                  </div>
                )}

                <div className="pt-6 border-t">
                  <div className="flex items-center gap-2 mb-1">
                    <BarChart3 className="h-4 w-4 text-orange-500" />
                    <h3 className="text-sm font-semibold">Google Tag Manager</h3>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">
                    Container do GTM para carregar Google Analytics (GA4), Google Ads, LinkedIn Insight ou
                    outras tags nas <strong>páginas públicas</strong> deste cliente. Nada é injetado no painel.
                  </p>
                  <Label htmlFor="gtm-container-id-input" className="text-sm font-semibold">
                    GTM Container ID <span className="text-slate-400 font-normal">(opcional)</span>
                  </Label>
                  <Input
                    id="gtm-container-id-input"
                    type="text"
                    placeholder="Ex.: GTM-57HD5QM5"
                    value={settings?.gtm_container_id || ''}
                    onChange={(e) => setSettings({ ...settings, gtm_container_id: e.target.value.toUpperCase() })}
                    disabled={!isAdmin}
                    className="mt-1.5 max-w-md font-mono"
                    data-testid="gtm-container-id-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Formato <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">GTM-XXXXXXX</code>. Encontrado no{' '}
                    <a href="https://tagmanager.google.com/" target="_blank" rel="noreferrer" className="underline text-primary">Google Tag Manager</a>{' '}
                    dentro do seu container. Deixe em branco para <strong>desativar</strong>.
                  </p>
                  {settings?.gtm_container_id && (
                    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 flex items-center gap-2">
                      <Check className="h-4 w-4" />
                      <span>
                        GTM ativo — o container carrega automaticamente nas páginas públicas deste cliente.
                      </span>
                    </div>
                  )}
                </div>

                {isAdmin && (
                  <div className="flex justify-end gap-2 pt-2 border-t">
                    {hasSettingsChanges && (
                      <Button variant="outline" onClick={handleResetSettings}>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Descartar
                      </Button>
                    )}
                    <Button
                      onClick={handleSaveSettings}
                      disabled={!hasSettingsChanges || saving}
                      data-testid="save-integrations-btn"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Salvando...' : 'Salvar integrações'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="lists" className="mt-6 space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Listas de tipos e categorias usadas em formulários do sistema.
              </p>
              {isAdmin && (
                <div className="flex gap-2">
                  {hasSettingsChanges && (
                    <Button variant="outline" onClick={handleResetSettings} data-testid="reset-settings-btn">
                      <RotateCcw className="h-4 w-4 mr-2" /> Descartar
                    </Button>
                  )}
                  <Button onClick={handleSaveSettings} disabled={!hasSettingsChanges || saving} data-testid="save-settings-btn">
                    <Save className="h-4 w-4 mr-2" /> {saving ? 'Salvando...' : 'Salvar alterações'}
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {SECTIONS.map((section) => (
                <ListEditor
                  key={section.key}
                  title={section.title}
                  description={section.description}
                  items={settings?.[section.key] || []}
                  onChange={(newItems) => handleSectionChange(section.key, newItems)}
                  disabled={!isAdmin}
                />
              ))}
            </div>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Estados de atuação</CardTitle>
                <CardDescription>
                  Selecione os estados onde o cliente atua. Apenas estes estados (e suas cidades) aparecerão nos formulários de cadastro de lideranças, links e apoiadores. Deixe vazio para permitir todos.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AllowedStatesEditor
                  selected={settings?.allowed_states || []}
                  onChange={(arr) => setSettings({ ...settings, allowed_states: arr })}
                  disabled={!isAdmin}
                />
              </CardContent>
            </Card>

            <Card className="mt-6 border-emerald-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                  Objetivo da campanha
                </CardTitle>
                <CardDescription>
                  Esta configuração é usada na página <strong>Metas</strong> para puxar os votos válidos do TSE por cidade e sugerir a meta de apoiadores.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="cargo_objetivo" className="text-sm font-semibold">
                    Cargo objetivo
                  </Label>
                  <select
                    id="cargo_objetivo"
                    value={settings?.cargo_objetivo || ''}
                    onChange={(e) => setSettings({ ...settings, cargo_objetivo: e.target.value })}
                    disabled={!isAdmin}
                    className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="cargo-objetivo-select"
                  >
                    <option value="">— Escolha um cargo —</option>
                    <option value="presidente">Presidente (2022)</option>
                    <option value="governador">Governador (2022)</option>
                    <option value="senador">Senador (2022)</option>
                    <option value="deputado_federal">Deputado Federal (2022)</option>
                    <option value="deputado_estadual">Deputado Estadual (2022)</option>
                    <option value="deputado_distrital">Deputado Distrital (2022)</option>
                    <option value="prefeito">Prefeito (2024)</option>
                    <option value="vereador">Vereador (2024)</option>
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    O cargo escolhido determina o universo de "votos válidos" de referência para cálculo de meta de apoiadores.
                  </p>
                </div>

                <div>
                  <Label htmlFor="meta_share_pct" className="text-sm font-semibold">
                    Share padrão sugerido (%)
                  </Label>
                  <Input
                    id="meta_share_pct"
                    type="number"
                    min={1}
                    max={100}
                    step={0.5}
                    value={settings?.meta_share_pct ?? 20}
                    onChange={(e) => setSettings({ ...settings, meta_share_pct: parseFloat(e.target.value) || 0 })}
                    disabled={!isAdmin}
                    className="mt-1.5 max-w-xs"
                    data-testid="meta-share-pct-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Percentual padrão dos votos válidos sugerido como meta de apoiadores. <strong>Ranges típicos:</strong> Vereador 5-8%, Prefeito 35-50%, Dep. Federal 1-3%, Senador/Governador 25-40%, Presidente 30%+. Você pode editar a meta individual de cada cidade depois.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
